import {
  AttentionBlock,
  Button,
  Kpi,
  PageHeader,
  PageTitle,
  Skeleton,
  StatusTag,
  plural,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import {
  STEP_LABELS,
  pendingStepCount,
  useOnboarding,
  type Onboarding,
  type OnboardingStepKey,
} from '@/features/onboarding/api'
import { saveFile } from '@/features/patients/download'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  useExportCsv,
  usePendingItems,
  usePeriod,
  useSavePeriod,
  useToday,
  type Period,
} from './api'
import { ClinicsList } from './clinics-list'
import { dateHeading } from './labels'
import { PendingBlock } from './pending-block'
import { PeriodResult } from './period-result'
import { TodayAgenda } from './today-agenda'

/**
 * Dashboard (docs/areas/dashboard.md; arquétipo "visão do dia"). De cima para baixo: data e local;
 * o lembrete da implantação (RN-ADM-80, 90); "Exige sua atenção" com fio plum, primeiro; o resultado
 * do período com a evolução (gestora e root, T32) ou o número de pendências e a agenda do dia
 * (recepção e prestadora). Abaixo de 900px a composição é "Hoje" (RN-DSH-22). Root sem clínica vê a
 * lista de clínicas (RN-DSH-23).
 */
export function DashboardPage() {
  const session = useSession()
  if (session.isGlobalRoot) return <GlobalDashboard />
  return <ClinicDashboard />
}

function GlobalDashboard() {
  return (
    <>
      <PageHeader crumbs={[{ label: 'Westhetics' }, { label: 'Dashboard' }]} />
      <Page wide>
        <PageTitle
          title="Clínicas"
          subtitle="Pendências e resultado de cada clínica, sem soma. Escolha uma para abrir o painel dela."
        />
        <div className="mt-6">
          <ClinicsList />
        </div>
      </Page>
    </>
  )
}

function ClinicDashboard() {
  const session = useSession()
  const isDesk = useBreakpoint('desk')
  const canSeePeriod = session.canManageClinic
  const onboarding = useOnboarding({ enabled: session.canManageClinic })
  const pending = usePendingItems()
  const today = useToday(undefined, { enabled: !isDesk || !canSeePeriod })
  const [period, setPeriod] = useState<Period | undefined>(undefined)
  const [practitionerId, setPractitionerId] = useState<string | null>(null)
  const periodQuery = usePeriod(
    { period, practitionerId: practitionerId ?? undefined },
    { enabled: isDesk && canSeePeriod },
  )
  const savePeriod = useSavePeriod()
  const exportCsv = useExportCsv()
  const activePeriod = period ?? periodQuery.data?.period.period ?? 'week'
  const clinicName = session.profile?.activeClinic?.name ?? ''
  const pendingTotal = pending.data?.meta.total ?? 0

  function changePeriod(next: Period) {
    setPeriod(next)
    savePeriod.mutate(next, { onError: (error) => toast(errorMessage(error)) })
  }

  function exportNow() {
    exportCsv.mutate(
      { period: activePeriod, practitionerId: practitionerId ?? undefined },
      {
        onSuccess: (file) => saveFile(file, 'resultado.csv'),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  const reminder = session.canManageClinic ? (
    <OnboardingReminder
      onboarding={onboarding.data}
      error={onboarding.isError ? onboarding.error : null}
      onRetry={() => void onboarding.refetch()}
    />
  ) : null

  const pendingBlock = pending.isError ? (
    <LoadError
      title="Não conseguimos carregar as pendências"
      error={pending.error}
      onRetry={() => void pending.refetch()}
    />
  ) : pending.isPending ? (
    <div role="status" aria-label="Carregando as pendências">
      <Skeleton className="h-[96px] w-full" />
    </div>
  ) : (
    <PendingBlock groups={pending.data.data} total={pending.data.meta.total} />
  )

  // Abaixo de 900px a tela inicial é a operação do dia (RN-DSH-22).
  if (!isDesk) {
    return (
      <>
        <PageHeader crumbs={[{ label: 'Hoje' }]} />
        <Page>
          <div className="flex flex-col gap-6">
            {reminder}
            {pendingBlock}
            <TodayAgenda
              data={today.data}
              isPending={today.isPending}
              error={today.isError ? today.error : null}
              onRetry={() => void today.refetch()}
              showNote
            />
          </div>
        </Page>
      </>
    )
  }

  return (
    <>
      <PageHeader crumbs={[{ label: 'Administração' }, { label: 'Dashboard' }]} />
      <Page wide>
        <PageTitle title={dateHeading(new Date())} meta={clinicName || undefined} />
        <div className="mt-6 flex flex-col gap-6">
          {reminder}
          {pendingBlock}
          {canSeePeriod ? (
            <PeriodResult
              period={activePeriod}
              onPeriodChange={changePeriod}
              practitionerId={practitionerId}
              onPractitionerChange={setPractitionerId}
              data={periodQuery.data}
              isPending={periodQuery.isPending}
              error={periodQuery.isError ? periodQuery.error : null}
              onRetry={() => void periodQuery.refetch()}
              onExport={exportNow}
              isExporting={exportCsv.isPending}
            />
          ) : (
            <>
              <Kpi
                hero
                label="Pendências abertas"
                value={pendingTotal}
                note={
                  pendingTotal === 0 ? 'nada pede atenção hoje' : 'o que pede atenção hoje, acima'
                }
                tone={
                  pending.data && pending.data.meta.byLevel.critical > 0 ? 'critical' : 'default'
                }
              />
              <TodayAgenda
                data={today.data}
                isPending={today.isPending}
                error={today.isError ? today.error : null}
                onRetry={() => void today.refetch()}
              />
            </>
          )}
        </div>
      </Page>
    </>
  )
}

/** As etapas que faltam e a saída para cada uma. Concluída, o bloco não existe. */
function OnboardingReminder({
  onboarding,
  error,
  onRetry,
  className,
}: {
  onboarding: Onboarding | undefined
  error: unknown
  onRetry: () => void
  className?: string
}) {
  if (error) {
    return (
      <LoadError
        className={className}
        title="Não conseguimos conferir a implantação"
        error={error}
        onRetry={onRetry}
      />
    )
  }
  if (!onboarding) {
    return (
      <div role="status" aria-label="Carregando a implantação" className={className}>
        <Skeleton className="h-[120px] w-full" />
      </div>
    )
  }
  if (onboarding.isCompleted) return null

  const pending = pendingStepCount(onboarding)
  return (
    <AttentionBlock
      variant="lilac"
      className={className}
      title={`Falta pouco para emitir o primeiro documento · ${plural(pending, 'etapa')}`}
    >
      <p>
        {onboarding.skippedAt
          ? 'Você deixou a implantação para depois. Cada etapa continua de onde parou.'
          : 'A clínica funciona inteira; a implantação é o que a deixa pronta para o primeiro documento.'}
      </p>
      <ul className="divide-hairline mt-3 divide-y">
        {onboarding.steps.map((step) => (
          <li key={step.key} className="flex items-center justify-between gap-4 py-2">
            <span className="font-display text-ink text-[13px] leading-[1.3] font-normal">
              {STEP_LABELS[step.key]}
            </span>
            <span className="flex items-center gap-4">
              <StatusTag level={step.status === 'done' ? 'done' : 'attention'}>
                {step.status === 'done' ? 'Feita' : 'Falta'}
              </StatusTag>
              <Button variant="text" className="text-[12px]" asChild>
                <Link to={routes.onboardingStep(step.key)} aria-label={stepActionLabel(step)}>
                  {step.status === 'done' ? 'Rever' : 'Continuar'}
                </Link>
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <Button className="max-desk:min-h-touch" asChild>
          <Link to={routes.onboarding}>Continuar a implantação</Link>
        </Button>
      </div>
    </AttentionBlock>
  )
}

/** "Continuar Procedimentos" · "Rever Empresa": o nome acessível de cada saída. */
function stepActionLabel(step: { key: OnboardingStepKey; status: 'done' | 'pending' }) {
  return `${step.status === 'done' ? 'Rever' : 'Continuar'} ${STEP_LABELS[step.key]}`
}
